import { Column } from "primereact/column";
import { DataTable, DataTableExpandedRows } from "primereact/datatable";
import { Tag } from "primereact/tag";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useGetBenefitComparisonChecksQuery } from "store/queries/benefitComparison";
import {
  IComparisonCheck,
  IComparisonChecksRequest,
} from "types/BenefitComparison/BenefitComparison";

import {
  formatComparisonValue,
  INCOMPLETE_REASON_LABELS,
  PATTERN_LABELS,
} from "./benefitComparisonWording";
import { BenefitVerdictPanel } from "./BenefitVerdictPanel";
import { PverifyRequestLink } from "./RemitAccuracyLinks";
import { formatConfidence } from "./remitAccuracyWording";

const PAGE_SIZE = 50;

/** CLE-2820 PR 4 — the expanded row: the verdict panel for that check × benefit. Module level so the table does not
 * remount it on every render. */
const verdictExpansion = (row: IComparisonCheck) => (
  <div className="p-3 surface-50">
    <BenefitVerdictPanel
      pverifyResponseId={row.pverifyResponseId}
      benefit={row.benefit}
      options={row.verdictOptions}
      verdict={row.verdict}
      remitValue={row.remit.value}
      remitConflict={row.remitConflict}
    />
  </div>
);

const orderBody = (row: IComparisonCheck) =>
  row.orderId && (
    <Link
      to={`/orders/${row.orderId}`}
      target="_blank"
      rel="noopener noreferrer"
    >
      Estimate
    </Link>
  );

/**
 * CLE-2820 PR 3 — the checks behind a count or a cluster: legacy, engine and AI side by side, with both pVerify ids,
 * the estimate when one references the check, and whether a remit exists.
 *
 * CLE-2820 PR 4 — each row EXPANDS into its verdict panel: the fixed question, one button per distinct value, "None of
 * these" and "Can't tell". The Verdict column shows what has been recorded, and flags a remit that disagrees with it.
 */
export function BenefitComparisonChecks({
  request,
  showAccount = false,
}: {
  request: IComparisonChecksRequest;
  showAccount?: boolean;
}) {
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState<DataTableExpandedRows>({});
  const requestKey = JSON.stringify(request);
  useEffect(() => {
    setOffset(0);
    setExpanded({});
  }, [requestKey]);
  const { data, isFetching } = useGetBenefitComparisonChecksQuery({
    ...request,
    limit: PAGE_SIZE,
    offset,
  });
  const page = data?.data;
  const value = (amount: number | null) =>
    amount === null ? (
      <span className="text-color-secondary">no value</span>
    ) : (
      formatComparisonValue(amount, request.benefit)
    );

  const checkBody = (row: IComparisonCheck) => (
    <div className="flex flex-column gap-1">
      <PverifyRequestLink requestId={row.pverifyRequestId} />
      <span className="text-xs text-color-secondary">
        {row.pverifyResponseId}
      </span>
    </div>
  );
  const statusBody = (row: IComparisonCheck) => (
    <span className="white-space-nowrap">
      {row.pattern
        ? PATTERN_LABELS[row.pattern]
        : row.incompleteReason &&
          INCOMPLETE_REASON_LABELS[row.incompleteReason]}
      {row.zeroVsAbsent && (
        <span className="block text-xs text-color-secondary">
          $0 = no value
        </span>
      )}
    </span>
  );
  const remitBody = (row: IComparisonCheck) => {
    if (!row.remit.exists)
      return <span className="text-color-secondary">—</span>;
    return row.remit.value === null
      ? "Remit, not scored"
      : value(row.remit.value);
  };

  const aiBody = (row: IComparisonCheck) => (
    <span className="white-space-nowrap">
      {value(row.aiValue)} · {formatConfidence(row.aiConfidence)}
    </span>
  );

  const verdictBody = (row: IComparisonCheck) => {
    if (!row.verdict)
      return <span className="text-color-secondary">Not reviewed</span>;
    return (
      <span className="white-space-nowrap">
        {row.verdict.verdict === "VALUE"
          ? value(row.verdict.value)
          : "Can’t tell"}
        {row.remitConflict && (
          <Tag
            className="ml-2"
            severity="warning"
            value="Remit differs"
            data-testid="benefitComparison.checks.remitConflict"
          />
        )}
      </span>
    );
  };

  return (
    <DataTable
      value={page?.rows ?? []}
      dataKey="pverifyResponseId"
      expandedRows={expanded}
      onRowToggle={(event) => setExpanded(event.data as DataTableExpandedRows)}
      rowExpansionTemplate={verdictExpansion}
      lazy
      paginator
      rows={PAGE_SIZE}
      first={offset}
      totalRecords={page?.total ?? 0}
      onPage={(event) => setOffset(event.first)}
      loading={isFetching}
      emptyMessage="No checks."
      data-testid="benefitComparison.checks"
    >
      <Column expander style={{ width: "3rem" }} />
      <Column
        header="Checked"
        body={(row: IComparisonCheck) => row.checkedAt.slice(0, 10)}
      />
      {showAccount && <Column field="accountName" header="Account" />}
      <Column
        header="Payer · Plan"
        body={(row: IComparisonCheck) =>
          `${row.payerName} · ${row.planName ?? "No plan name"}`
        }
      />
      <Column header="Pverify Request" body={checkBody} />
      <Column header="" body={statusBody} />
      <Column
        header="Legacy"
        body={(row: IComparisonCheck) => value(row.legacyValue)}
      />
      <Column
        header="Engine"
        body={(row: IComparisonCheck) => value(row.engineValue)}
      />
      <Column header="AI" body={aiBody} />
      <Column header="Remit" body={remitBody} />
      <Column header="Verdict" body={verdictBody} />
      <Column header="" body={orderBody} />
    </DataTable>
  );
}
